import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal, Select } from 'antd';

import DialogLoadState from '../common/DialogLoadState';
import { Skeleton } from '../loading';
import useCharts from '../../hooks/useCharts';
import { CHART_SCOPE } from '../../services/charts';
import { placeableCharts } from '../../utils/dashboards';
import { normalizeId } from '../../utils/ids';

/**
 * **Add chart**, while arranging (charts A6, plan §6.5): one of the
 * collection's charts the dashboard may hold — a dashboard for everyone only
 * charts pinned for everyone (its own collection's; ⚑ d08),
 * a personal one those and the person's own — or **New chart**, to the
 * builder. The charts are read when the window opens (Reports' list, never
 * run). The pick goes to the page's draft, which runs it before Done.
 *
 *   dashboard  the dashboard being arranged (its tier decides the offer)
 *   placed     chart ids already on it (said beside the name; still allowed)
 *   onAdd      (chart) => void
 *   onNewChart () => void
 */
export default function AddChartModal({ open, dashboard, collectionId, placed, onAdd, onNewChart, onClose }) {
	const { charts, loading, error, reload } = useCharts(collectionId, { enabled: open });
	const [picked, setPicked] = useState(null);
	useEffect(() => { if (open) setPicked(null); }, [open]);

	const offer = useMemo(() => placeableCharts(dashboard, { charts }), [dashboard, charts]);
	const placedSet = useMemo(() => new Set((placed || []).map((id) => normalizeId(id))), [placed]);
	const options = useMemo(() => {
		const groups = [];
		const add = (label, list) => {
			if (!list.length) return;
			groups.push({
				label,
				options: list.map((chart) => {
					const id = normalizeId(chart._id);
					const name = chart.name || 'Untitled chart';
					return { value: id, label: placedSet.has(id) ? `${name} (on this dashboard)` : name };
				}),
			});
		};
		add('Pinned for everyone', offer.filter((c) => c.scope === CHART_SCOPE.COLLECTION));
		add('Yours', offer.filter((c) => c.scope !== CHART_SCOPE.COLLECTION));
		return groups;
	}, [offer, placedSet]);

	const forEveryone = dashboard && dashboard.scope === CHART_SCOPE.COLLECTION;
	const add = () => {
		const chart = offer.find((c) => normalizeId(c._id) === picked);
		if (!chart) return;
		onAdd(chart);
		onClose();
	};

	const loaded = offer.length > 0 || (!loading && !error);
	const body = (
		<DialogLoadState
			loaded={loaded}
			loading={loading}
			error={Boolean(error)}
			isEmpty={!offer.length}
			empty={<p className='add-chart-modal__empty'>{forEveryone ? 'No charts pinned for everyone yet. Choose New chart to make one, then pin it for everyone.' : 'No charts yet. Choose New chart to make one.'}</p>}
			label='charts'
			onRetry={reload}
			skeleton={<Skeleton variant='form-field' count={1} />}
			minHeight={96}
		>
			<label className='name-chart-modal__label' htmlFor='add-chart-select'>Chart</label>
			<Select
				id='add-chart-select'
				className='add-chart-modal__select'
				showSearch
				optionFilterProp='label'
				placeholder='Choose a chart'
				value={picked}
				options={options}
				onChange={setPicked}
				virtual={false}
			/>
		</DialogLoadState>
	);

	return (
		<Modal
			open={open}
			title='Add chart'
			onCancel={onClose}
			destroyOnClose
			className='add-chart-modal'
			footer={[
				<Button key='new' type='link' className='add-chart-modal__new' onClick={onNewChart}>New chart</Button>,
				<Button key='cancel' onClick={onClose}>Cancel</Button>,
				<Button key='add' type='primary' disabled={!picked} onClick={add}>Add</Button>,
			]}
		>
			{forEveryone ? <p className='name-chart-modal__hint'>A dashboard for everyone holds only charts pinned for everyone.</p> : null}
			{body}
		</Modal>
	);
}
